import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import { 
  Building2, 
  Briefcase, 
  Users, 
  Lightbulb,
  HelpCircle,
  ArrowRight,
  ArrowLeft,
  Send,
  MapPin,
  Phone,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const profileTypes = [
  { id: 'startup', label: 'Startup', icon: Lightbulb, desc: 'Je lance mon activité' },
  { id: 'pme', label: 'PME / TPE', icon: Building2, desc: 'Je développe mon entreprise' },
  { id: 'grand-compte', label: 'Grand Compte', icon: Briefcase, desc: 'Projet d\'envergure' },
  { id: 'agence', label: 'Agence', icon: Users, desc: 'Partenariat ou sous-traitance' },
  { id: 'autre', label: 'Autre', icon: HelpCircle, desc: 'Projet personnel ou autre' },
];

const projectTypes = [
  'Site web vitrine',
  'Site e-commerce',
  'Application mobile',
  'Plateforme SaaS',
  'Logiciel interne',
  'Programme parrainage',
  'Jeu de fidélisation',
  'Autre projet'
];

export const ContactPage = () => {
  const [step, setStep] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleProfileSelect = (profileId) => {
    setSelectedProfile(profileId);
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.project_type || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        profile_type: selectedProfile,
        ...formData
      });
      setSubmitted(true);
      toast.success('Message envoyé avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
      console.error('Contact error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      <SEO 
        title="Contact"
        description="Contactez WEBISULA pour votre projet de creation de site internet, application mobile ou solution digitale en Corse. Reponse rapide garantie."
        path="/contact"
      />
      {/* Hero */}
      <section className="pt-8 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label mb-4"
          >
            Contact
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-6"
            data-testid="contact-title"
          >
            Parlons de votre <span className="text-gradient">projet</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-white/60 max-w-2xl mx-auto"
          >
            Sélectionnez votre profil pour nous envoyer un message adapté à vos besoins.
          </motion.p>
        </div>
      </section>

      {/* Form Section */}
      <section className="px-6 lg:px-12 pb-24">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {submitted ? (
              /* Success State */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card rounded-3xl p-12 text-center"
                data-testid="contact-success"
              >
                <div className="w-20 h-20 bg-[#8B5A8B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-[#8B5A8B]" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">
                  Message envoyé !
                </h2>
                <p className="text-white/60 mb-8">
                  Merci pour votre demande. Nous vous répondrons dans les plus brefs délais (généralement sous 24h).
                </p>
                <button
                  onClick={() => { setSubmitted(false); setStep(1); setSelectedProfile(''); setFormData({ name: '', email: '', phone: '', company: '', project_type: '', message: '' }); }}
                  className="outline-btn"
                  data-testid="contact-new-message"
                >
                  Envoyer un autre message
                </button>
              </motion.div>
            ) : step === 1 ? (
              /* Step 1: Profile Selection */
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="contact-step-1"
              >
                <h2 className="text-2xl font-bold text-white text-center mb-8">
                  Vous êtes...
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {profileTypes.map((profile) => (
                    <button
                      key={profile.id}
                      onClick={() => handleProfileSelect(profile.id)}
                      className="profile-card text-left hover:scale-[1.02] transition-transform"
                      data-testid={`profile-${profile.id}`}
                    >
                      <div className="w-12 h-12 bg-[#8B5A8B]/20 rounded-xl flex items-center justify-center mb-4">
                        <profile.icon className="text-[#8B5A8B]" size={24} />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {profile.label}
                      </h3>
                      <p className="text-white/60 text-sm">{profile.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-center text-white/40 text-sm mt-8">
                  Temps de réponse moyen : 24h en jours ouvrés
                </p>
              </motion.div>
            ) : (
              /* Step 2: Contact Form */
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                data-testid="contact-step-2"
              >
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
                  data-testid="contact-back-btn"
                >
                  <ArrowLeft size={18} />
                  Retour
                </button>

                <div className="glass-card rounded-3xl p-8 md:p-12">
                  <div className="flex items-center gap-3 mb-8">
                    {profileTypes.find(p => p.id === selectedProfile)?.icon && (
                      <div className="w-12 h-12 bg-[#8B5A8B]/20 rounded-xl flex items-center justify-center">
                        {React.createElement(profileTypes.find(p => p.id === selectedProfile).icon, { className: 'text-[#8B5A8B]', size: 24 })}
                      </div>
                    )}
                    <div>
                      <p className="text-white/60 text-sm">Profil sélectionné</p>
                      <p className="text-white font-semibold">
                        {profileTypes.find(p => p.id === selectedProfile)?.label}
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Nom complet *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Jean Dupont"
                          required
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="jean@example.com"
                          required
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Téléphone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="06 12 34 56 78"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Entreprise</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Nom de votre entreprise"
                          data-testid="contact-company"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">Type de projet *</label>
                      <select
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        data-testid="contact-project-type"
                      >
                        <option value="">Sélectionnez un type de projet</option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">Votre message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-input min-h-[150px] resize-none"
                        placeholder="Décrivez votre projet, vos besoins, vos délais..."
                        required
                        data-testid="contact-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full glow-btn flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <span>Envoi en cours...</span>
                      ) : (
                        <>
                          <Send size={18} />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Contact Info */}
      <section className="px-6 lg:px-12 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card rounded-2xl p-6 text-center" data-testid="contact-info-location">
              <div className="w-12 h-12 bg-[#8B5A8B]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-[#8B5A8B]" size={24} />
              </div>
              <p className="text-white font-medium mb-1">Localisation</p>
              <p className="text-white/60 text-sm">Ajaccio, Corse</p>
            </div>
            <a href="tel:0759948289" className="glass-card rounded-2xl p-6 text-center hover:border-[#8B5A8B]/50 transition-colors" data-testid="contact-info-phone">
              <div className="w-12 h-12 bg-[#8B5A8B]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Phone className="text-[#8B5A8B]" size={24} />
              </div>
              <p className="text-white font-medium mb-1">Téléphone</p>
              <p className="text-white/60 text-sm">07 59 94 82 89</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
